import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { MainListItems } from './ListItems';
import { HeaderReducer, actions, SelectedMenu } from '@/redux/modules/Header';
import { connect, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { StateReducer } from '@/redux/modules/reducer';
import { useMediaQuery, SwipeableDrawer } from '@mui/material';

const drawerWidth = 280;

export const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(9),
                },
            }),
        },
    }),
);

const LeftMenu = (props: Pick<HeaderReducer, "rolesData" | "menuBarOpen" | "selectedMenu" | "queueMenuBarOpen">) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const onSelectMenu = (menu: SelectedMenu) => {
        if (menu.path == '/queue') {
            dispatch(actions.openQueueMenuBar(!props.queueMenuBarOpen));
        }
        dispatch(actions.selectMenu(menu));
        dispatch(actions.openQueueMenuBar(false));

        // Auto close drawer on mobile after selection
        if (isMobile) {
            dispatch(actions.openMenuBar(false));
        }

        navigate(menu.path);
    };

    const handleDrawerToggle = () => {
        dispatch(actions.openMenuBar(!props.menuBarOpen));
    };

    const drawerContent = (
        <>
            <Toolbar
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: [1],
                    minHeight: 64,
                }}
            >
                <IconButton onClick={handleDrawerToggle}>
                    <ChevronLeftIcon />
                </IconButton>
            </Toolbar>
            <Divider />
            <List
                component="nav"
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(0, 0, 0, 0.15) transparent',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.15)',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    },
                }}
            >
                <MainListItems
                    selectedMenu={props.selectedMenu}
                    onSelectMenu={onSelectMenu}
                    rolesData={props.rolesData}
                />
            </List>
        </>
    );

    // Mobile: Use SwipeableDrawer for better UX
    if (isMobile) {
        return (
            <SwipeableDrawer
                anchor="left"
                open={!!props.menuBarOpen}
                onClose={handleDrawerToggle}
                onOpen={() => dispatch(actions.openMenuBar(true))}
                swipeAreaWidth={20}
                disableBackdropTransition={true}
                disableDiscovery={true}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
            >
                {drawerContent}
            </SwipeableDrawer>
        );
    }

    // Desktop: Use persistent drawer
    return (
        <Drawer variant="permanent" open={props.menuBarOpen} anchor="left">
            {drawerContent}
        </Drawer>
    );
};

function mapStateToProps(state: StateReducer) {
    const header: HeaderReducer = state?.header;

    return {
        rolesData: header?.rolesData,
        menuBarOpen: header?.menuBarOpen,
        selectedMenu: header?.selectedMenu,
        queueMenuBarOpen: header?.queueMenuBarOpen
    };
}

export default connect(mapStateToProps, null)(LeftMenu);